import { router } from '@inertiajs/core';
import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import maxBy from 'lodash/maxBy';
import { ReactNode, useEffect } from 'react';
import { Folder } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, BarProps, CartesianGrid, CartesianGridProps, Line, LineChart, LineProps, ResponsiveContainer, ResponsiveContainerProps, Tooltip, TooltipProps, XAxis, XAxisProps, YAxis, YAxisProps } from 'recharts';
import { CategoricalChartProps } from 'recharts/types/chart/generateCategoricalChart';

import { assetUrl } from '../../Shared/helpers';
import { useLocale } from '../../Shared/locale';
import { Campaign, Project } from '../../Shared/types';
import { Card } from '../../Shared/UI/Card';
import { ColorBadge } from '../../Shared/UI/ColorBadge';
import Picker, { PickerItem } from '../../Shared/UI/Picker';
import { CampaignLogo } from '../Campaigns/CampaignLogo';
import { useRouter } from '../router';
import { Charity, Donation, SharedProps } from '../types';
import Container from '../UI/Container';
import Header from '../UI/Header';
import HeaderTitle from '../UI/HeaderTitle';
import Layout from '../UI/Layout';
import { DashboardLayout } from './DashboardLayout';

const defaultChartProps: {
  container: Partial<ResponsiveContainerProps>;
  chart: Partial<CategoricalChartProps>;
  grid: Partial<CartesianGridProps>;
  xAxis: Partial<Omit<XAxisProps, 'ref'>>;
  yAxis: Partial<Omit<YAxisProps, 'ref'>>;
  bar: Omit<BarProps, 'ref'>;
  line: Omit<LineProps, 'ref'>;
} = {
  container: {
    width: '100%',
    height: 250,
  },
  chart: {
    height: 250,
    margin: {
      top: 15,
      right: 15,
      left: 0,
      bottom: 15,
    },
  },
  grid: {
    stroke: '#e5e7eb',
    strokeDasharray: '5 5',
    vertical: false,
  },
  xAxis: {
    type: 'category',
    tick: { fontSize: 10 },
    stroke: '#616774',
    dy: 10,
  },
  yAxis: {
    tick: { fontSize: 10 },
    strokeWidth: 0,
  },
  bar: {
    dataKey: 'value',
  },
  line: {
    type: 'monotone',
    dataKey: 'value',
    strokeWidth: 2,
    dot: false,
  },
};

interface DashboardPageProps extends SharedProps {
  fundraisingAmounts: {
    date: string;
    value: number;
  }[];
  campaignCounts: {
    date: string;
    value: number;
  }[];
  totals: {
    overall: {
      funds_raised: number;
      donations_count: number;
      campaigns_count: number;
    };
    today: {
      funds_raised: number;
      donations_count: number;
      campaigns_count: number;
    };
  };
  recentCampaigns: (Campaign & {
    project: Project;
  })[];
  topCampaigns: (Campaign & {
    project: Project;
    recent_funds_raised: number;
  })[];
  recentDonations: (Donation & {
    campaign: Campaign & {
      project: Project;
    };
    email: string | null;
  })[];
  projects: PickerItem[];
}

export default function DashboardPage({
  fundraisingAmounts,
  campaignCounts,
  totals,
  recentCampaigns,
  topCampaigns,
  recentDonations,
  projects,
  charity,
  params,
}: DashboardPageProps) {
  const { t } = useTranslation();
  const { formatNumber, formatCurrency, formatDate } = useLocale();

  // Refresh dashboard every 15 seconds.
  useEffect(() => {
    const timer = window.setInterval(() => {
      router.reload();
    }, 15_000);

    return () => window.clearInterval(timer);
  });

  const activityListMaxLength = Math.max(recentCampaigns.length, topCampaigns.length, recentDonations.length);

  const Chart = params.project ? LineChart : BarChart;

  return (
    <>
      <Header>
        <Container>
          <div className="items-center justify-between sm:flex">
            <HeaderTitle>
              {t('backend:dashboard')}
            </HeaderTitle>
            <div className="w-full sm:w-[300px]">
              <Picker
                name="project"
                items={projects}
                selected={typeof params.project === 'string' ? params.project : null}
                icon={<Folder />}
                optional
                placeholder={(t('backend:all_projects'))}
              />
            </div>
          </div>
        </Container>
      </Header>
      <Container className="space-y-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <Card panels header spacing="sm" className="flex flex-col">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-500">
                {t('backend:revenue')}
              </h4>
              <span className="text-xs text-slate-500">
                {params.project ? t('backend:total') : t('backend:monthly')}
              </span>
            </div>
            <div className="flex items-center justify-center flex-1">
              {fundraisingAmounts.length === 0 && (
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={120} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              )}
              {fundraisingAmounts.length > 0 && (
                <ResponsiveContainer {...defaultChartProps.container}>
                  <Chart
                    {...defaultChartProps.chart}
                    data={fundraisingAmounts}
                  >
                    <CartesianGrid {...defaultChartProps.grid} />
                    <XAxis
                      {...defaultChartProps.xAxis}
                      dataKey="date"
                      tickFormatter={(value) => formatDate(value, 'display_month_short')}
                    />
                    <YAxis
                      {...defaultChartProps.yAxis}
                      tickFormatter={(value) => formatCurrency(value, {
                        minimumFractionDigits: 0, maximumFractionDigits: 0,
                      })}
                      width={formatCurrency(maxBy(fundraisingAmounts, 'value')?.value || 0, {
                        minimumFractionDigits: 0, maximumFractionDigits: 0,
                      }).length * 6.3 + 20}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(226, 228, 233, 0.3)' }}
                      content={({ active, payload, label }: TooltipProps<number, number>) => {
                        if (active && payload && payload.length) {
                          return (
                            <Card border className="!p-2 text-sm">
                              <strong className="block text-blue-500">
                                {formatDate(label, 'display_month')}
                              </strong>
                              {t('backend:funds_raised', { amount: formatCurrency(payload[0].value || 0) })}
                            </Card>
                          );
                        }
                      }}
                    />
                    {params.project ? (
                      <Line
                        {...defaultChartProps.line}
                        stroke="#7575c2"
                      />
                    ) : (
                      <Bar
                        {...defaultChartProps.bar}
                        fill="#7575c2"
                      />
                    )}
                  </Chart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          <Card panels header spacing="sm" className="flex flex-col">
            <div className="flex justify-between items-center">
              <h4 className="font-medium text-slate-500">
                {t('backend:campaigns')}
              </h4>
              <span className="text-xs text-slate-500">
                {params.project ? t('backend:total') : t('backend:monthly')}
              </span>
            </div>
            <div className="flex items-center justify-center flex-1">
              {campaignCounts.length === 0 && (
                <div className="flex items-center justify-center flex-1">
                  <div className="text-center text-slate-400">
                    <img src={assetUrl('images/dashboard-empty.svg')} width={120} className="block mx-auto mb-4" />
                    {t('backend:no_data_yet')}
                  </div>
                </div>
              )}
              {campaignCounts.length > 0 && (
                <ResponsiveContainer {...defaultChartProps.container}>
                  <Chart
                    {...defaultChartProps.chart}
                    data={campaignCounts}
                  >
                    <CartesianGrid {...defaultChartProps.grid} />
                    <XAxis
                      {...defaultChartProps.xAxis}
                      dataKey="date"
                      tickFormatter={(value) => formatDate(value, 'display_month_short')}
                    />
                    <YAxis
                      {...defaultChartProps.yAxis}
                      tickFormatter={(value) => formatNumber(value)}
                      width={formatNumber(maxBy(campaignCounts, 'value')?.value || 0).length * 6.3 + 20}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(226, 228, 233, 0.3)' }}
                      content={({ active, payload, label }: TooltipProps<number, number>) => {
                        if (active && payload && payload.length) {
                          return (
                            <Card border className="!p-2 text-sm">
                              <strong className="block text-emerald-500">
                                {formatDate(label, 'display_month')}
                              </strong>
                              {t('backend:n_campaigns', {
                                value: formatNumber(payload[0].value || 0), count: payload[0].value || 0,
                              })}
                            </Card>
                          );
                        }
                      }}
                    />
                    {params.project ? (
                      <Line
                        {...defaultChartProps.line}
                        stroke="#00a050"
                      />
                    ) : (
                      <Bar
                        {...defaultChartProps.bar}
                        fill="#00a050"
                      />
                    )}
                  </Chart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>

        <Card className="grid text-center divide-y sm:divide-y-0 sm:divide-x sm:grid-cols-3 bg-gradient-to-t from-slate-50" spacing="none">
          <div className="p-6 space-y-2">
            <h4 className="font-medium text-slate-500">
              {t('backend:raised')}
            </h4>
            <div className="text-2xl">
              {formatCurrency(totals.overall.funds_raised, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <span
              className={classNames(
                'inline-block px-1 py-0.5 text-sm font-medium',
                totals.today.funds_raised > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
              )}
            >
              {t('backend:plus_value_today', {
                value: formatCurrency(totals.today.funds_raised, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
              })}
            </span>
          </div>
          <div className="p-6 space-y-2">
            <h4 className="font-medium text-slate-500">
              {t('backend:campaigns')}
            </h4>
            <div className="text-2xl">
              {formatNumber(totals.overall.campaigns_count)}
            </div>
            <span
              className={classNames(
                'inline-block px-1 py-0.5 text-sm font-medium',
                totals.today.campaigns_count > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
              )}
            >
              {t('backend:plus_value_today', { value: formatNumber(totals.today.campaigns_count) })}
            </span>
          </div>
          <div className="p-6 space-y-2">
            <h4 className="font-medium text-slate-500">
              {t('backend:donations')}
            </h4>
            <div className="text-2xl">
              {formatNumber(totals.overall.donations_count)}
            </div>
            <span
              className={classNames(
                'inline-block px-1 py-0.5 text-sm font-medium',
                totals.today.donations_count > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
              )}
            >
              {t('backend:plus_value_today', { value: formatNumber(totals.today.donations_count) })}
            </span>
          </div>
        </Card>

        <div className="grid gap-6 sm:grid-cols-3">
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:recent_campaigns')}
              </h4>
            </div>
            {recentCampaigns.map((campaign) => (
              <CampaignCard campaign={campaign} charity={charity} key={campaign.id} />
            ))}
            {recentCampaigns.length > 0 && recentCampaigns.length < activityListMaxLength && (
              <div className="flex flex-1 bg-slate-50/50"></div>
            )}
            {recentCampaigns.length === 0 && (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={120} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              </div>
            )}
          </Card>
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:recent_top_campaigns')}
              </h4>
            </div>
            {topCampaigns.map((campaign) => (
              <CampaignCard campaign={campaign} charity={charity} key={campaign.id} />
            ))}
            {topCampaigns.length > 0 && topCampaigns.length < activityListMaxLength && (
              <div className="flex flex-1 bg-slate-50/50"></div>
            )}
            {topCampaigns.length === 0 && (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={120} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              </div>
            )}
          </Card>
          <Card panels header spacing="sm" className="flex flex-col">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:recent_donations')}
              </h4>
            </div>
            {recentDonations.map((donation) => (
              <DonationCard donation={donation} charity={charity} key={donation.id} />
            ))}
            {recentDonations.length > 0 && recentDonations.length < activityListMaxLength && (
              <div className="flex flex-1 bg-slate-50/50"></div>
            )}
            {recentDonations.length === 0 && (
              <div className="flex items-center justify-center flex-1">
                <div className="text-center text-slate-400">
                  <img src={assetUrl('images/dashboard-empty.svg')} width={120} className="block mx-auto mb-4" />
                  {t('backend:no_data_yet')}
                </div>
              </div>
            )}
          </Card>
        </div>
      </Container>
    </>
  );
}

DashboardPage.layout = (page: ReactNode) => <Layout><DashboardLayout>{page}</DashboardLayout></Layout>;

const CampaignCard = ({
  campaign,
  charity,
}: {
  campaign: Campaign & {
    project: Project;
    recent_funds_raised?: number;
  };
  charity: Charity;
}) => {
  const { formatCurrency, formatDate } = useLocale();
  const { routes } = useRouter();

  return (
    <div className="grid grid-cols-[32px_1fr_fit-content(140px)] gap-3 items-center !py-4">
      <div>
        <CampaignLogo campaign={campaign} charity={charity} />
      </div>
      <div className="min-w-0 leading-snug">
        <Link href={routes.campaign_page(campaign.id)} className="block text-sm font-medium truncate">
          {campaign.title}
        </Link>
        <div className="text-xs truncate text-slate-400">
          {campaign.project.title}
        </div>
      </div>
      <div className="min-w-0 text-right leading-snug">
        <div className="text-sm font-medium">
          {formatCurrency(campaign.funds_raised)}
        </div>
        {typeof campaign.recent_funds_raised !== 'undefined' && (
          <div className="text-xs text-emerald-500">
            + {formatCurrency(campaign.recent_funds_raised)}
          </div>
        )}
        {typeof campaign.recent_funds_raised === 'undefined' && (
          <div className="text-xs text-slate-400">
            {formatDate(campaign.created_at, 'display_date_time')}
          </div>
        )}
      </div>
    </div>
  );
};

const DonationCard = ({
  donation,
  charity,
}: {
  donation: Donation & {
    campaign: Campaign & {
      project: Project;
    };
    email: string | null;
  };
  charity: Charity;
}) => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useLocale();
  const { routes } = useRouter();

  return (
    <div className="grid grid-cols-[32px_1fr_fit-content(140px)] gap-3 items-center !py-4">
      <div>
        <ColorBadge
          color={charity.brand_color}
          name={donation.name || donation.email || t('backend:anonymous')}
        />
      </div>
      <div className="min-w-0 leading-snug">
        <Link href={routes.donation_page(donation.id)} className="text-sm font-medium truncate">
          {donation.name || donation.email || t('backend:anonymous')}
        </Link>
        <div className="text-xs truncate text-slate-400">
          {donation.campaign?.project.title}
        </div>
      </div>
      <div className="min-w-0 text-right leading-snug">
        <div className="text-sm font-medium">
          {formatCurrency(donation.amount)}
        </div>
        <div className="text-xs text-slate-400">
          {formatDate(donation.created_at, 'display_date_time')}
        </div>
      </div>
    </div>
  );
};
